import React from "react";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Query, queryOps, socialDatasetId } from "system/query/Query";
import { QueryField, QueryInfo, KeywordInputType, getKeywordInputType } from "system/query/QueryInfo";
import QuerySegmentOptionsDialog from "../../dialogParts/QuerySegmentOptionsDialog";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QuerySegment } from "system/query/QuerySegment";
import { QueryLeafNode } from "system/query/QueryLeafNode";
import { QueryFieldListSelector } from "../../dialogParts/QueryFieldListSelector";
import { QueryLeafNodeFields } from "./QueryLeafNodeFields";
import QueryLeafNodeOpSelector from "./QueryLeafNodeOpSelector";
import { getAllowedFieldTypesByOp, getAllowedLeafOpByFieldType, queryOpIsCompare } from "system/query/QueryUtility";
import { Alert, Box, Button, Paper, Tooltip } from "@mui/material";
import { QueryValueInput } from "../../dialogParts/QueryValueInput";
import { MultiValueTextInput } from "components/uiElements/inputs/MultiValueTextInput";
import { QueryMultiValueInput } from "../../common/QueryMultiValueInput";
import { QueryBranch } from "system/query/QueryBranch";
import { QuerySpan } from "system/query/QuerySpan";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import BooleanQueryField from "components/queries/BooleanQueryField";
dayjs.extend(duration);

// Helper to convert a relative string like "-P14D" (past) or "P14D" (future) to an absolute date
function parseRangeValue(value: string | number | boolean | null | undefined) {
  if (typeof value === "string") {
    const now = dayjs();
    if (value.startsWith("-")) {
      // Past date: Subtract duration
      const period = value.substring(1);
      const parsedDate = now.subtract(dayjs.duration(period)); // "-P14D" becomes 14 days ago
      return parsedDate.toISOString();
    } else if (value.startsWith("P")) {
      // Future date: Add duration
      const period = value;
      const parsedDate = now.add(dayjs.duration(period)); // "P14D" becomes 14 days in the future
      return parsedDate.toISOString();
    }
  }
  // If it's not a relative date, return it as is
  return value;
}

export interface QueryLeafNodeBuilderDialogProps {
  isOpen: boolean;
  setDialogIsOpen: (isOpen: boolean) => void;
  parent?: QuerySegment | Query;
  queryLeafNode: QueryLeafNode;
  queryInfo: QueryInfo;
  updateQuery: (query: Query) => void;
  originalQuery: Query;
  inSpan?: boolean;
  spanSide?: string;
}

export function QueryLeafNodeBuilderDialog(props: QueryLeafNodeBuilderDialogProps): JSX.Element {
  const {i18n} = useTranslation();

  // Create a new QueryLeafNode instance from the props.queryLeafNode
  const queryLeafNode = new QueryLeafNode(props.queryLeafNode);

  // Helper to replace the old leaf node in the parent with the new one
  function setNewQueryLeafNode(newQueryLeafNode: QueryLeafNode) {
    // If there is no parent, we can't replace the leaf node
    if (!props.parent) return;

    // If parent is a branch with values array
    const branch = props.parent as QueryBranch;
    if (Array.isArray(branch.values)) {
      const oldQueryLeafNode = branch.values.indexOf(props.queryLeafNode);
      if (oldQueryLeafNode !== -1) {
        branch.values[oldQueryLeafNode] = newQueryLeafNode;
      }
    }

    // If parent and the node is just the root query, just replace the query
    if (props.parent === props.originalQuery?.query && props.queryLeafNode === props.parent) {
      props.originalQuery.query = newQueryLeafNode;
    }

    // If parent is a span (left/right)
    if ((branch as QuerySpan).left === props.queryLeafNode) {
      (branch as QuerySpan).left = newQueryLeafNode;
    }
    if ((branch as QuerySpan).right === props.queryLeafNode) {
      (branch as QuerySpan).right = newQueryLeafNode;
    }
  }

  const defaultFields:string[] = queryLeafNode.fields as string[] || (queryLeafNode.field && [queryLeafNode.field]) || [];
  const [fields, setFields] = useState<string[]>(defaultFields);

  let defaultValues = props.queryLeafNode.values || (props.queryLeafNode.value ? [props.queryLeafNode.value] : []);
  // The AI query assist sometimes generates values as an array of arrays (e.g. [["anger"], ["joy"], ["fear"]]).  
  // The API has been made to handle this format as well so AI queries don't break things as often.  If we are
  // editing these values in the UI we also need to be able to handle them.  The simplest way to do that is to
  // flatten the array of arrays back into the more ideal single array of values (e.g. ["anger", "joy", "fear"]).
  defaultValues = defaultValues.flat();

  const [values, setValues] = useState<any[]>(defaultValues);

  const [labels, setLabels] = useState<string[]>(queryLeafNode.labels || []);

  const [value, setValue] = useState<string | number | boolean | null | undefined>(queryLeafNode.value);

  const [lower, setLower] = useState<string | number | boolean | null | undefined>(queryLeafNode.lower || undefined);
  const [upper, setUpper] = useState<string | number | boolean | null | undefined>(queryLeafNode.upper || undefined);
  const [comparison, setComparison] = useState<string | undefined>(queryLeafNode.comparison || undefined);

  const [op, setOp] = useState<string | undefined>(queryLeafNode.op || undefined);

  const firstFieldId = fields[0];
  const firstField = props.queryInfo.fields.find(field => field.id === firstFieldId);
  const firstFieldType = firstField?.type;
  const allowedFieldOps = firstFieldType ? getAllowedLeafOpByFieldType(firstFieldType): undefined;
  const allowedOpFieldTypes = op ? getAllowedFieldTypesByOp(op as queryOps) : undefined;

  function setFieldIds(newFields?: string[]) {
    if (!newFields?.length) {
      setValues([]);
      setLabels([]);
      setFields([]);
      setLower(undefined);
      setUpper(undefined);
      setComparison(undefined);
      setOp(undefined);
      return;
    }

    // Set a default value (if applicable)
    const selectedField = props.queryInfo.fields.find((field) => field.id === newFields[0]);
    const defaultOption = selectedField?.options?.[0];
    if (defaultOption) {
      // If the new selected field has a default option make sure it is set.
      setValues([defaultOption.id]);
      setLabels([defaultOption.label]);
      setFields([]);
      setLower(undefined);
      setUpper(undefined);
      setComparison(undefined);
    } else {
      // If the selected field has no default and the type is the same we can keep the user's
      // input.  Otherwise, we need to make sure we reset values.
      const currentFieldId = fields[0];
      const currentField = props.queryInfo.fields.find((field) => field.id === currentFieldId);
      if (currentField?.type !== selectedField?.type) {
        setValues([]);
        setLabels([]);
        setFields([]);
        setLower(undefined);
        setUpper(undefined);
        setComparison(undefined);
      }
    }

    const type = selectedField?.type;
    if (type && !op) {
      if (["integer", "decimal", "timestamp"].includes(type)) {
        setOp(queryOps.range);
      }
      if (["text", "keyword", "keywords", "uid"].includes(type)) {
        setOp(queryOps.contains);
      }
      // if (["latitude_longitude"].includes(type)) {
      //   setOp(queryOps.match);
      // }
      if (["boolean"].includes(type)) {
        setOp(queryOps.equals);
      }
    }
    setFields(newFields || []);
  }

  function preSetOp(newOp: string | undefined) {
    if (newOp === op) {
      return;
    }
    if (!newOp || (allowedFieldOps && !allowedFieldOps.includes(newOp))) {
      // This op is not allowed for this field type, reset the form
      setValues([]);
      setLabels([]);
      setFields([]);
      setLower(undefined);
      setUpper(undefined);
      setComparison(undefined);
    }
    setOp(newOp);
  }

  const isSocialDataset = (props.queryInfo?.dataset?.id === socialDatasetId);
  // If we have a social dataset, we can add the special "bio"+"description"+"body" fields to the list of fields
  const combinedSocialTextField = {
    id: "__title_description_body",
    label: i18n.t("query-form.title-description-body"),
    agg_supported: true,
    query_supported: true,
    type: "text",
    _social_field_category: "text",
  } as QueryField;
  const allFields = [...props.queryInfo.fields];

  if (isSocialDataset) {
    allFields.push(combinedSocialTextField);
  }

  const isComparison = queryOpIsCompare(op as queryOps) || op === queryOps.equals;
  const isRange = op === queryOps.range;
  const isContains = op == queryOps.contains;
  const isBoolean = op === queryOps.boolean;

  function submit() {
    if ((!fields || !fields.length || !op) && !props.inSpan) {
      return;
    }
    // Range operator output - will convert to a compare operator if only one value is set
    if (op === queryOps.range) {
      queryLeafNode.resetToDefaults && queryLeafNode.resetToDefaults();
      queryLeafNode.field = fields[0];
      queryLeafNode.op = queryOps.range;
      if (!lower && upper) {
        // We only have an upper value, so we'll convert this to a less than or equal to comparison
        queryLeafNode.op = queryOps.lessThanOrEqual;
        queryLeafNode.value = upper;
      } else if (lower && !upper) {
        // We only have a lower value, so we'll convert this to a greater than or equal to comparison
        queryLeafNode.op = queryOps.greaterThanOrEqual;
        queryLeafNode.value = lower;
      } else if (lower && upper) {
        // We have both values, so we'll use the Range operator (Requires: lower and upper values, and comparison)

        // We need to first check if the values are relative dates
        const parsedLower = parseRangeValue(lower);
        const parsedUpper = parseRangeValue(upper);

        // If the lower value is greater than the upper value, we'll swap them
        if (parsedLower && parsedUpper && (parsedLower < parsedUpper)) {
          queryLeafNode.lower = lower;
          queryLeafNode.upper = upper;
        } else {
          queryLeafNode.lower = upper;
          queryLeafNode.upper = lower;
        }

        // If a comparison is set, we'll use that
        if (comparison) {
          queryLeafNode.comparison = comparison;
        }
      }
    } else if (op === queryOps.contains || props.inSpan) {
      // Contains can have multiple fields and multiple values
      queryLeafNode.resetToDefaults && queryLeafNode.resetToDefaults();
      queryLeafNode.op = queryOps.contains;
      if (!props.inSpan) {
        queryLeafNode.fields = fields;
      }

      // Combine labels and values into a single object array
      const combinedLabelsAndValues = values.map((value, index) => {
        let label = value;
        if (labels.length > index) {
          label = labels[index];
        }
        return {
          label: label,
          value: value
        };
      });

      // trim whitespace, remove any empty values, and remove duplicates
      const cleanedValuesAndLabels = combinedLabelsAndValues.map(valAndLabel => {
        const value = valAndLabel.value;
        const label = valAndLabel.label;
        // Trim extra whitespace from strings
        if (typeof value === "string") {
          return {
            value: value.trim(),
            label: label
          };
        }
        return valAndLabel;
      // Filter empty strings
      }).filter(valAndLabel => {
        if (typeof valAndLabel.value === "string") {
          return valAndLabel.value.length > 0;
        }
        return true;
      // Remove duplicates
      }).filter((valAndLabel, index, self) => {
        return self.findIndex(o => o.value === valAndLabel.value) === index;
      });
      // extract values and labels again
      const cleanedLabels = cleanedValuesAndLabels.map(valAndLabel => {
        return valAndLabel.label;
      });
      const cleanedValues = cleanedValuesAndLabels.map(valAndLabel => {
        return valAndLabel.value;
      });
      queryLeafNode.labels = cleanedLabels;
      queryLeafNode.values = cleanedValues;
    } else if (op === queryOps.boolean) {
      // Booleans can have multiple fields, but only one value
      queryLeafNode.resetToDefaults && queryLeafNode.resetToDefaults();
      queryLeafNode.op = queryOps.boolean;
      queryLeafNode.fields = fields;
      queryLeafNode.value = value || null;
    } else if (op === queryOps.equals || queryOpIsCompare(op as queryOps)) {
      // Comparison operators can only have one field and one value
      queryLeafNode.resetToDefaults && queryLeafNode.resetToDefaults();
      queryLeafNode.op = op as queryOps;
      queryLeafNode.field = fields[0];
      queryLeafNode.value = value;
      queryLeafNode.label = labels[0];
    }
    // Replace the old leaf node in the parent with the new one
    setNewQueryLeafNode(queryLeafNode);

    // Update the query with the new leaf node
    props.updateQuery(props.originalQuery);
    props.setDialogIsOpen(false);
  }

  const isTextContains = firstField?.type === "text" || firstField?.type === "decimal" || firstField?.type === "integer";

  const isMultiValueField = useMemo(() => {
    return !!firstField && getKeywordInputType(firstField) === KeywordInputType.string
  }, [firstField]);

  return (
    <QuerySegmentOptionsDialog
      isOpen={props.isOpen}
      parent={props.parent}
      querySegment={props.queryLeafNode}
      updateQuery={props.updateQuery}
      originalQuery={props.originalQuery}
      maxWidth="sm"
      onKeyDown={(event: React.KeyboardEvent) => {
        // If the user hits enter, we'll submit the form
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          submit();
        }
      }}
      onCancel={(event: any) => {
        event.stopPropagation();
        props.setDialogIsOpen(false)
      }}
      onConfirm={(event: any) => {
        event.stopPropagation();
        event.preventDefault();
        submit();
      }}
      title={((!fields || !fields.length) && !props.inSpan) ? i18n.t("query-form.field-select") : i18n.t("query-form.add-filter")}
      >
      {((!fields || !fields.length) && !props.inSpan) &&
        <>
          <QueryFieldListSelector
            fields={allFields}
            allowedFieldTypes={allowedOpFieldTypes}
            selectedFieldId={undefined}
            requireQuerySupported={true}
            setSelectedFieldId={(fieldId: string) => {
              // When a field is selected, initialize the default value and field.
              const selectedField = props.queryInfo.fields.find((field) => field.id === fieldId);
              const defaultOption = selectedField?.options?.[0];
              if (defaultOption) {
                setValues([defaultOption.id]);
                setLabels([defaultOption.label]);
              }
              if (fieldId === combinedSocialTextField.id) {
                setFieldIds(["title", "description", "body"]);
                return;
              }
              setFieldIds([fieldId]);
            }}
          />
          {!!allowedOpFieldTypes?.length &&
            <Alert severity="info" sx={{ mt: 1 }}>
              {i18n.t("query-form.incompatible-fields-hidden-warning")}
              <br/>
              <Button
                onClick={() => preSetOp(undefined)}
              >{i18n.t("query-form.clear-selected-op")}</Button>
            </Alert>
          }
        </>
      }
      {((!!fields && !!fields.length && !!op) || props.inSpan) &&
        <>
        {!props.inSpan &&
          <Paper sx={{ p: 2, pt: 0.5, mb: 2 }}>
            <SimpleFormLabel>
              {i18n.t("query-form.fields-select")}
            </SimpleFormLabel>
            <QueryLeafNodeFields
              queryInfo={props.queryInfo}
              fields={fields}
              setFieldIds={setFieldIds}
              op={op}
            />
          </Paper>
        }
        <Paper sx={{ p: 2, pt: 1, mb: 1 }}>
        <QueryLeafNodeOpSelector
            allowedFieldOps={allowedFieldOps}
            op={(props.inSpan ? queryOps.contains : op) as string}
            inSpan={props.inSpan}
            setOp={preSetOp} />
          {isComparison &&
            <QueryValueInput
              includeNowOption={true}
              isDateInclusive={true}
              queryInfo={props.queryInfo}
              isFocused={true}
              selectedField={props.queryInfo.fields.find(field => field.id === firstFieldId)}
              value={value}
              setValue={setValue}
              setLabels={setLabels} />
          }
          {isBoolean &&
            <BooleanQueryField
              content={value as string || ""}
              height={40}
              setContent={(content: string) => setValue(content)}
            />
          }
          {isRange &&
            <>
            <QueryValueInput
              queryInfo={props.queryInfo}
              selectedField={firstField}
              useSimpifiedRelativeDates={true}
              isFocused={true}
              value={lower}
              setValue={setLower}
              isDateInclusive={true} />
            <Tooltip title={firstField?.type === "timestamp" ? i18n.t("query-form.less-than-tooltip") : i18n.t("query-form.less-than-equal-tooltip")}>
              <Box><SimpleFormLabel>
                {firstField?.type === "timestamp" ? i18n.t("query-form.end-date") : i18n.t("query-form.upper-value")}
              </SimpleFormLabel></Box>
            </Tooltip>
            <QueryValueInput
              includeNowOption={true}
              queryInfo={props.queryInfo}
              selectedField={firstField}
              useSimpifiedRelativeDates={true}
              value={upper}
              setValue={setUpper} />
            </>
          }
          {((isContains && isTextContains) || props.inSpan || isMultiValueField) &&
            <>
              <MultiValueTextInput
                values={values}
                setValues={(newValues: any[]) => {
                  setValues(newValues);
                  setLabels(newValues);
                }}
                enforceDecimal={firstField?.type === "decimal"}
                enforceInteger={firstField?.type === "integer"}
              />
            </>
          }
          {(isContains && !isTextContains && !isMultiValueField) &&
            <>
              {firstFieldType === "timestamp" &&
                <Alert severity="info">
                  {i18n.t("query-form.contains-timestamp-warning")}
                </Alert>
              }
              <QueryMultiValueInput
                queryInfo={props.queryInfo}
                fields={fields}
                values={values}
                setValues={setValues}
                labels={labels}
                setLabels={setLabels}
              />
            </>
          }
          </Paper>
        </>
      }
    </QuerySegmentOptionsDialog>
  );
}